import { useConfirm } from "../context/ConfirmContext";
import { AsyncButton } from "../components/AsyncButton";
import { LoadingOverlay } from "../components/LoadingOverlay";
import { useBlockingDownload } from "../hooks/useBlockingDownload";
import React, { useState, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getEmployees, updateEmployee, updateUserProfile, assignManager,
  getEmployeeDocuments, uploadEmployeeDocument, deleteEmployeeDocument, downloadEmployeeDocument,
  getLetterTypes, getLetters, generateLetter, updateLetter, deleteLetter, downloadLetterPdf,
  getDocTemplates,
  requestLetterSignature, resendLetterSignatureRequest, getLetterSignatureEvents,
  getLetterCountersigners,
  createInvite, resendInvite,
} from "../api";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import api from "../api";
import { DateInput } from "../components/DateInput";
import DepartmentSelect from "../components/DepartmentSelect";
import { formatDate, formatDateTime, todayStr } from "../utils/dateFormat";
import { useTenantTimezone } from "../hooks/useTenantTimezone";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:30801";


function SetupLinkButton({ userId, name }: { userId: number; name?: string }) {
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();
  const confirm = useConfirm();

  async function generate() {
    const ok = await confirm(
      `This will email ${name || "this employee"} a one-time setup link, in addition to copying it here. Continue?`,
      { title: "Send setup link", confirmLabel: "Send & Copy", danger: false }
    );
    if (!ok) return;
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/employees/${userId}/setup-link`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
      });
      const d = await r.json();
      if (!r.ok) { toast(d.detail || "Failed to generate link", "error"); return; }
      await navigator.clipboard.writeText(d.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    } finally {
      setLoading(false);
    }
  }

  return (
    <button onClick={generate} disabled={loading} style={{
      background: copied ? "#dcfce7" : "white",
      border: `1.5px solid ${copied ? "#86efac" : "#e2e8f0"}`,
      borderRadius: 8, padding: "7px 14px", fontSize: 13,
      fontWeight: 600, cursor: loading ? "not-allowed" : "pointer",
      color: copied ? "#166534" : "#475569",
    }}>
      {loading ? "Sending…" : copied ? "✓ Sent & copied!" : "🔗 Send setup link"}
    </button>
  );
}

function ResendInviteButton({ userId, name }: { userId: number; name?: string }) {
  const [sending, setSending] = useState(false);
  const { toast } = useToast();
  const confirm = useConfirm();

  async function handle(e: React.MouseEvent) {
    e.stopPropagation();
    const ok = await confirm(`Resend the invite email to ${name || "this employee"}?`, {
      title: "Resend invite", confirmLabel: "Resend", danger: false,
    });
    if (!ok) return;
    setSending(true);
    try {
      const data = await resendInvite(userId);
      if (data.email_sent === false) {
        toast(`Invite created but email failed — share the link manually. Error: ${data.email_error || "unknown"}`, "error");
      } else {
        toast("Invite email resent successfully", "success");
      }
    } catch {
      toast("Failed to resend invite", "error");
    } finally {
      setSending(false);
    }
  }

  return (
    <button onClick={handle} disabled={sending} style={{
      padding: "4px 10px", fontSize: 11, background: "#fefce8",
      border: "1px solid #fde047", borderRadius: 6, cursor: sending ? "not-allowed" : "pointer",
      color: "#854d0e", fontWeight: 600,
    }}>
      {sending ? "Sending…" : "Resend"}
    </button>
  );
}

const NAVY = "#1e3a8a";
const TEAL = "#0f766e";
const TITLE_COLOR = "#0f172a";

const ROLE_BADGE: Record<string, { bg: string; text: string; border: string }> = {
  admin:           { bg: "#eff6ff", text: "#1d4ed8", border: "#bfdbfe" },
  admin_assistant: { bg: "#f0fdf4", text: "#166534", border: "#86efac" },
  hr:              { bg: "#fdf4ff", text: "#7e22ce", border: "#e9d5ff" },
  recruiter:       { bg: "#fdf4ff", text: "#7e22ce", border: "#e9d5ff" },
  contractor:      { bg: "#fff7ed", text: "#c2410c", border: "#fed7aa" },
  finance:         { bg: "#fef9c3", text: "#854d0e", border: "#fde047" },
  employee:        { bg: "#f1f5f9", text: "#475569", border: "#cbd5e1" },
};

const DEPT_COLORS: Record<string, string> = {
  Engineering: "#3b82f6", Marketing: "#f59e0b", Sales: "#10b981",
  HR: "#8b5cf6", Finance: "#ef4444", Operations: "#64748b",
};

type Tab = "list" | "profile" | "documents" | "letters";

export default function EmployeesPage() {
  const { user } = useAuth();
  const tz = useTenantTimezone();
  const qc = useQueryClient();
  const confirm = useConfirm();
  const { toast } = useToast();
  const { downloading, download } = useBlockingDownload();
  const isAdmin = user?.role === "admin" || user?.role === "admin_assistant" || user?.role === "hr";

  const [tab, setTab] = useState<Tab>("list");
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState<any>({});
  const [search, setSearch] = useState("");
  const [deptFilter, setDeptFilter] = useState("");
  const [sortBy, setSortBy] = useState("name_asc");
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [hoveredId, setHoveredId] = useState<number | null>(null);
  const [uploadName, setUploadName] = useState("");
  const [uploadType, setUploadType] = useState("contract");
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [importResult, setImportResult] = useState<any>(null);
  const [updateExisting, setUpdateExisting] = useState(false);
  const [importing, setImporting] = useState(false);
  const csvInputRef = useRef<HTMLInputElement>(null);

  // Add Employee modal
  const [showInvite, setShowInvite] = useState(false);
  const [inviteResult, setInviteResult] = useState<string | null>(null);
  const [inviting, setInviting] = useState(false);
  const BLANK_INVITE = {
    name: "", email: "", role: "employee", designation: "", department: "",
    joining_date: new Date().toISOString().split("T")[0], employee_id: "", manager_id: "",
  };
  const [inviteForm, setInviteForm] = useState(BLANK_INVITE);

  function setIF(k: keyof typeof BLANK_INVITE) {
    return (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
      setInviteForm(f => ({ ...f, [k]: e.target.value }));
  }

  async function handleInvite(e: React.FormEvent) {
    e.preventDefault();
    if (!inviteForm.email.trim()) { toast("Email is required", "error"); return; }
    if (!inviteForm.name.trim()) { toast("Name is required", "error"); return; }
    const ok = await confirm(`Send an invite email to ${inviteForm.name.trim()} (${inviteForm.email.trim()})?`, {
      title: "Invite employee", confirmLabel: "Send Invite", danger: false,
    });
    if (!ok) return;
    setInviting(true);
    try {
      const payload: any = {
        email: inviteForm.email.trim(),
        name: inviteForm.name.trim(),
        role: inviteForm.role,
      };
      if (inviteForm.designation) payload.designation = inviteForm.designation;
      if (inviteForm.department) payload.department = inviteForm.department;
      if (inviteForm.joining_date) payload.joining_date = inviteForm.joining_date;
      if (inviteForm.employee_id) payload.employee_id = inviteForm.employee_id;
      if (inviteForm.manager_id) payload.manager_id = Number(inviteForm.manager_id);
      const data = await createInvite(payload);
      setInviteResult(data.invite_url);
      qc.invalidateQueries({ queryKey: ["employees"] });
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      toast(typeof detail === "string" ? detail : detail?.message ?? "Failed to generate invite link", "error");
    } finally {
      setInviting(false);
    }
  }

  function closeInviteModal() {
    setShowInvite(false);
    setInviteResult(null);
    setInviteForm(BLANK_INVITE);
  }

  const { data: employees = [] } = useQuery({ queryKey: ["employees"], queryFn: getEmployees });

  const { data: documents = [] } = useQuery({
    queryKey: ["employee-docs", selectedId],
    queryFn: () => getEmployeeDocuments(selectedId!),
    enabled: !!selectedId && tab === "documents",
  });

  // Letters state
  const [showLetterForm, setShowLetterForm] = useState(false);
  const [letterForm, setLetterForm] = useState<any>({ letter_type: "appointment", params: {} });
  const [editLetter, setEditLetter] = useState<any>(null);
  const [downloadingLetter, setDownloadingLetter] = useState<number | null>(null);

  const { data: letterTypes = [] } = useQuery({ queryKey: ["letter-types"], queryFn: getLetterTypes });
  // Templates the tenant has written for the letter type being generated. Empty
  // for a tenant that has never made one, which hides the select entirely so
  // nothing about the form changes for them (spec document-templates.md AC26).
  const { data: allDocTemplates = [] } = useQuery({
    queryKey: ["doc-templates", "hr_letter"],
    queryFn: () => getDocTemplates({ doc_family: "hr_letter" }),
  });
  const templateChoices = (allDocTemplates as any[]).filter(
    (tpl: any) => tpl.doc_type === letterForm.letter_type && tpl.is_active);
  const defaultTemplate = templateChoices.find((tpl: any) => tpl.is_default);
  const { data: letters = [] } = useQuery({
    queryKey: ["employee-letters", selectedId],
    queryFn: () => getLetters(selectedId!),
    enabled: !!selectedId && tab === "letters",
  });


  const updateMut = useMutation({
    mutationFn: async ({ id, data, profileData }: { id: number; data: object; profileData?: object }) => {
      await updateEmployee(id, data);
      if (profileData) await updateUserProfile(id, profileData);
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["employees"] }); setEditMode(false); toast("Profile updated", "success"); },
    onError: (e: any) => toast(e?.response?.data?.detail || "Failed to save changes", "error"),
  });

  const uploadDocMut = useMutation({
    mutationFn: ({ userId, fd }: { userId: number; fd: FormData }) => uploadEmployeeDocument(userId, fd),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["employee-docs", selectedId] }); setUploadName(""); setUploadFile(null); },
  });

  const deleteDocMut = useMutation({
    mutationFn: ({ userId, docId }: { userId: number; docId: number }) => deleteEmployeeDocument(userId, docId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["employee-docs", selectedId] }),
  });

  const generateLetterMut = useMutation({
    mutationFn: generateLetter,
    onSuccess: (data: any) => {
      qc.invalidateQueries({ queryKey: ["employee-letters", selectedId] });
      setShowLetterForm(false);
      setEditLetter(data);
    },
  });

  const updateLetterMut = useMutation({
    mutationFn: ({ id, data }: { id: number; data: object }) => updateLetter(id, data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["employee-letters", selectedId] }); setEditLetter(null); },
  });

  const deleteLetterMut = useMutation({
    mutationFn: deleteLetter,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["employee-letters", selectedId] }),
  });

  // The toast used to claim "the employee has been notified" unconditionally,
  // including when the email bounced — on the local cluster every one of them
  // did. Report the channels that actually worked (AC25).
  // "info" rather than "error": the request itself succeeded and every signer
  // can still reach the document from To Sign — only the email leg failed.
  // Names who was missed: a contract has two signers, and "the email could not
  // be sent" is useless if it doesn't say to whom.
  function deliveryMessage(d: any): [string, "success" | "info"] {
    if (!d) return ["Signature requested", "success"];
    const people: any[] = d.recipients || [];
    const names = (rs: any[]) => rs.map(r => r.name || "the signer").join(" and ");
    if (d.email) {
      return [people.length > 1
        ? `Signatures requested — ${names(people)} have been emailed and notified in-app`
        : "Signature requested — emailed and notified in-app", "success"];
    }
    const unreachable = people.filter(r => !r.email && !r.in_app);
    if (unreachable.length) {
      return [`Signature requested, but ${names(unreachable)} could not be notified — tell them to open To Sign`, "info"];
    }
    const noEmail = people.filter(r => !r.email);
    return [`Signature requested and shown in To Sign, but the email to ${names(noEmail)} could not be sent`, "info"];
  }

  // A contract needs a company signatory chosen before the request goes out
  // (AC18) — HR picks one in a small modal rather than the button firing blind.
  const [countersignFor, setCountersignFor] = useState<any>(null);
  const [countersignerId, setCountersignerId] = useState<number | "">("");
  const { data: countersigners = [] } = useQuery({
    queryKey: ["letter-countersigners"],
    queryFn: getLetterCountersigners,
    enabled: !!countersignFor,
  });

  const requestSignatureMut = useMutation({
    mutationFn: ({ id, countersignerId: cs }: { id: number; countersignerId?: number }) =>
      requestLetterSignature(id, cs),
    onSuccess: (res: any) => {
      qc.invalidateQueries({ queryKey: ["employee-letters", selectedId] });
      const [message, level] = deliveryMessage(res?.delivery);
      toast(message, level);
      setCountersignFor(null);
      setCountersignerId("");
    },
    onError: (e: any) => toast(e?.response?.data?.detail || "Failed to request signature", "error"),
  });

  // Package D: the backend has always exposed this timeline; nothing in the
  // product ever called it. Signature evidence you cannot read is not evidence.
  const [historyLetter, setHistoryLetter] = useState<any>(null);
  const { data: sigEvents = [], isLoading: sigEventsLoading } = useQuery({
    queryKey: ["letter-signature-events", historyLetter?.id],
    queryFn: () => getLetterSignatureEvents(historyLetter.id),
    enabled: !!historyLetter,
  });

  const resendSignatureMut = useMutation({
    mutationFn: resendLetterSignatureRequest,
    onSuccess: (res: any) => {
      const [message, level] = deliveryMessage(res?.delivery);
      toast(message.replace("Signature requested", "Reminder sent"), level);
    },
    onError: (e: any) => toast(e?.response?.data?.detail || "Failed to resend", "error"),
  });

  async function handleDownloadLetter(id: number) {
    setDownloadingLetter(id);
    try {
      const { blob, filename } = await downloadLetterPdf(id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
    } catch { toast("Failed to download PDF", "error"); }
    finally { setDownloadingLetter(null); }
  }

  const selectedEmployee = (employees as any[]).find((e: any) => e.id === selectedId);

  const departments = Array.from(new Set((employees as any[]).map((e: any) => e.department).filter(Boolean)));
  const filtered = (employees as any[]).filter((e: any) => {
    const q = search.toLowerCase();
    const matchSearch = !q || (e.name || "").toLowerCase().includes(q) ||
      (e.email || "").toLowerCase().includes(q) || (e.designation || "").toLowerCase().includes(q) ||
      (e.employee_id || "").toLowerCase().includes(q);
    const matchDept = !deptFilter || e.department === deptFilter;
    return matchSearch && matchDept;
  }).sort((a: any, b: any) => {
    if (sortBy === "name_asc")  return (a.name || "").localeCompare(b.name || "");
    if (sortBy === "name_desc") return (b.name || "").localeCompare(a.name || "");
    if (sortBy === "newest")    return (b.joining_date || "").localeCompare(a.joining_date || "");
    if (sortBy === "oldest")    return (a.joining_date || "").localeCompare(b.joining_date || "");
    return 0;
  });

  function openEmployee(emp: any) {
    setSelectedId(emp.id);
    setForm({ ...emp });
    setEditMode(false);
    setTab("profile");
  }

  async function handleUploadDoc() {
    if (!selectedId || !uploadName || !uploadFile) return;
    const fd = new FormData();
    fd.append("name", uploadName);
    fd.append("doc_type", uploadType);
    fd.append("file", uploadFile);
    uploadDocMut.mutate({ userId: selectedId, fd });
  }

  async function handleDownloadDoc(docId: number, name: string) {
    if (!selectedId) return;
    await download(async () => {
      const { blob, filename } = await downloadEmployeeDocument(selectedId, docId);
      return { blob, filename: filename || name };
    }, "Failed to download document");
  }

  async function handleCsvImport(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true); setImportResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("update_existing", String(updateExisting));
      const r = await api.post("/api/employees/bulk-import", fd);
      setImportResult(r.data);
      qc.invalidateQueries({ queryKey: ["employees"] });
    } catch (err: any) {
      setImportResult({ error: err.response?.data?.detail || "Import failed" });
    } finally {
      setImporting(false);
      if (csvInputRef.current) csvInputRef.current.value = "";
    }
  }

  return (
    <div>
      <LoadingOverlay active={
        updateMut.isPending || uploadDocMut.isPending || generateLetterMut.isPending ||
        updateLetterMut.isPending || deleteDocMut.isPending || deleteLetterMut.isPending ||
        requestSignatureMut.isPending ||
        downloadingLetter !== null || downloading
      } />
      <div className="page-header">
        <div>
          <h1 style={{ margin: 0, color: TITLE_COLOR, fontSize: 24, fontWeight: 700 }}>Employees</h1>
          <p style={{ margin: "4px 0 0", color: "#64748b", fontSize: 14 }}>{(employees as any[]).length} team members</p>
        </div>
        {!selectedId && isAdmin && (
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <input ref={csvInputRef} type="file" accept=".csv" style={{ display: "none" }} onChange={handleCsvImport} />
            <button onClick={() => {
              const csv = "email,name,role,designation,department,employee_id,joining_date,manager_email\njohn@company.com,John Smith,employee,Software Engineer,Engineering,EMP001,2024-01-15,manager@company.com\njane@company.com,Jane Doe,employee,QA Engineer,Engineering,EMP002,2024-02-01,manager@company.com\n";
              const a = document.createElement("a"); a.href = "data:text/csv," + encodeURIComponent(csv);
              a.download = "employee_import_template.csv"; a.click();
            }} style={{ background: "none", border: "1px solid #e2e8f0", borderRadius: 8, padding: "8px 14px", fontSize: 13, color: "#94a3b8", cursor: "pointer" }}>
              ⬇ Template
            </button>
            <label style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12, color: "#64748b", cursor: "pointer", userSelect: "none" as const }}
              title="When ticked, CSV rows whose email matches an existing employee in your company update that employee instead of being skipped.">
              <input type="checkbox" checked={updateExisting} onChange={e => setUpdateExisting(e.target.checked)} style={{ cursor: "pointer" }} />
              Update existing
            </label>
            <button onClick={() => csvInputRef.current?.click()} disabled={importing}
              style={{ background: "none", border: "1px solid #e2e8f0", borderRadius: 8, padding: "8px 14px", fontSize: 13, color: "#475569", cursor: "pointer" }}>
              {importing ? "Importing…" : "⬆ Import CSV"}
            </button>
            <button onClick={() => setShowInvite(true)}
              style={{ background: NAVY, color: "white", border: "none", borderRadius: 8, padding: "8px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
              + Add Employee
            </button>
          </div>
        )}
        {selectedId && (
          <div style={{ display: "flex", gap: 8 }}>
            {isAdmin && <SetupLinkButton userId={selectedId} name={selectedEmployee?.name} />}
            <button onClick={() => { setSelectedId(null); setTab("list"); }} style={outlineBtnStyle}>
              ← Back to list
            </button>
          </div>
        )}
      </div>

      {/* ── Add Employee Modal ── */}
      {showInvite && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 400, display: "flex", alignItems: "center", justifyContent: "center" }}
          onClick={closeInviteModal}>
          <div style={{ background: "white", borderRadius: 14, padding: 28, width: "min(560px, calc(100vw - 32px))", maxHeight: "92vh", overflowY: "auto", boxShadow: "0 24px 64px rgba(0,0,0,0.25)" }}
            onClick={e => e.stopPropagation()}>

            {!inviteResult ? (
              <>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 22 }}>
                  <div>
                    <h2 style={{ margin: 0, color: NAVY, fontSize: 18, fontWeight: 700 }}>Add Employee</h2>
                    <p style={{ margin: "4px 0 0", color: "#64748b", fontSize: 13 }}>An invite link will be sent / generated after saving.</p>
                  </div>
                  <button onClick={closeInviteModal} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#94a3b8", lineHeight: 1 }}>✕</button>
                </div>

                <form onSubmit={handleInvite} style={{ display: "grid", gap: 14 }}>
                  {/* Row 1: Name + Email */}
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                    <div>
                      <label style={labelStyle}>Full Name *</label>
                      <input required value={inviteForm.name} onChange={setIF("name")}
                        placeholder="Jane Smith" style={inputStyle} />
                    </div>
                    <div>
                      <label style={labelStyle}>Work Email *</label>
                      <input required type="email" value={inviteForm.email} onChange={setIF("email")}
                        placeholder="jane@company.com" style={inputStyle} />
                    </div>
                  </div>

                  {/* Row 2: Role + Employee ID */}
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                    <div>
                      <label style={labelStyle}>Role *</label>
                      <select value={inviteForm.role} onChange={setIF("role")} style={inputStyle}>
                        <option value="employee">Employee</option>
                        <option value="contractor">Contractor</option>
                        <option value="hr">HR</option>
                        <option value="recruiter">Recruiter</option>
                        <option value="finance">Finance</option>
                        <option value="admin_assistant">Admin Assistant</option>
                        <option value="admin">Admin</option>
                      </select>
                    </div>
                    <div>
                      <label style={labelStyle}>Employee ID <span style={{ fontWeight: 400, color: "#94a3b8" }}>(auto if blank)</span></label>
                      <input value={inviteForm.employee_id} onChange={setIF("employee_id")}
                        placeholder="EMP-001" style={inputStyle} />
                    </div>
                  </div>

                  {/* Row 3: Designation + Department */}
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                    <div>
                      <label style={labelStyle}>Designation / Job Title</label>
                      <input value={inviteForm.designation} onChange={setIF("designation")}
                        placeholder="Software Engineer" style={inputStyle} />
                    </div>
                    <div>
                      <label style={labelStyle}>Department</label>
                      <DepartmentSelect
                        value={inviteForm.department}
                        onChange={name => setInviteForm(f => ({ ...f, department: name }))}
                      />
                    </div>
                  </div>

                  {/* Row 4: Joining Date + Manager */}
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                    <div>
                      <label style={labelStyle}>Joining Date</label>
                      <DateInput value={inviteForm.joining_date} onChange={setIF("joining_date")} style={inputStyle} />
                    </div>
                    <div>
                      <label style={labelStyle}>Reports To (Manager)</label>
                      <select value={inviteForm.manager_id} onChange={setIF("manager_id")} style={inputStyle}>
                        <option value="">— No manager —</option>
                        {(employees as any[]).map((e: any) => (
                          <option key={e.id} value={e.id}>{e.name}{e.designation ? ` · ${e.designation}` : ""}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 6 }}>
                    <button type="button" onClick={closeInviteModal} style={outlineBtnStyle}>Cancel</button>
                    <button type="submit" disabled={inviting} style={primaryBtnStyle}>
                      {inviting ? "Adding…" : "Add & Generate Link"}
                    </button>
                  </div>
                </form>
              </>
            ) : (
              /* Success state */
              <div style={{ textAlign: "center", padding: "8px 0" }}>
                <div style={{ fontSize: 40, marginBottom: 12 }}>✅</div>
                <h3 style={{ margin: "0 0 6px", color: NAVY }}>Employee added!</h3>
                <p style={{ color: "#64748b", fontSize: 13, margin: "0 0 20px" }}>
                  Share this invite link with <strong>{inviteForm.name || inviteForm.email}</strong>. It expires in 7 days.
                </p>
                <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, padding: "12px 14px", marginBottom: 16, textAlign: "left" }}>
                  <code style={{ fontSize: 11, color: "#1e3a8a", wordBreak: "break-all" }}>{inviteResult}</code>
                </div>
                <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
                  <button onClick={() => { navigator.clipboard.writeText(inviteResult); toast("Link copied!", "success"); }}
                    style={primaryBtnStyle}>Copy Link</button>
                  <button onClick={() => { setInviteResult(null); setInviteForm(BLANK_INVITE); }}
                    style={outlineBtnStyle}>Add Another</button>
                  <button onClick={closeInviteModal} style={outlineBtnStyle}>Done</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Import result banner ── */}
      {importResult && (
        <div style={{
          background: importResult.error ? "#fef2f2" : "#f0fdf4",
          border: `1px solid ${importResult.error ? "#fecaca" : "#86efac"}`,
          borderRadius: 10, padding: "12px 16px", marginBottom: 16, fontSize: 13,
          color: importResult.error ? "#dc2626" : "#166534",
        }}>
          {importResult.error ? `❌ ${importResult.error}` : (
            <>
              <div>
                ✅ Imported {importResult.created} employees.
                {importResult.updated > 0 && ` ${importResult.updated} updated.`}
                {` ${importResult.skipped} skipped (already exist).`}
                {importResult.errors?.length > 0 && ` ${importResult.errors.length} row${importResult.errors.length === 1 ? "" : "s"} rejected:`}
                <button onClick={() => setImportResult(null)} style={{ marginLeft: 12, background: "none", border: "none", cursor: "pointer", color: "#166534", fontWeight: 700 }}>✕</button>
              </div>
              {importResult.updated > 0 && importResult.updated_emails?.length > 0 && (
                <div style={{ marginTop: 4, fontSize: 12, color: "#166534" }}>
                  Updated: {importResult.updated_emails.join(", ")}
                </div>
              )}
              {importResult.skipped > 0 && importResult.skipped_emails?.length > 0 && (
                <div style={{ marginTop: 4, fontSize: 12, color: "#166534" }}>
                  Skipped: {importResult.skipped_emails.join(", ")}
                </div>
              )}
              {importResult.errors?.length > 0 && (
                <div style={{ marginTop: 8, overflowX: "auto" as const }}>
                  <table style={{ borderCollapse: "collapse", fontSize: 12, background: "#fff", borderRadius: 6, minWidth: 380 }}>
                    <thead>
                      <tr style={{ color: "#991b1b", textAlign: "left" as const }}>
                        <th style={{ padding: "4px 10px", borderBottom: "1px solid #fecaca" }}>Row</th>
                        <th style={{ padding: "4px 10px", borderBottom: "1px solid #fecaca" }}>Email</th>
                        <th style={{ padding: "4px 10px", borderBottom: "1px solid #fecaca" }}>Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {importResult.errors.map((e: any, idx: number) => (
                        <tr key={idx} style={{ color: "#7f1d1d" }}>
                          <td style={{ padding: "3px 10px", borderBottom: "1px solid #fee2e2" }}>{e.row}</td>
                          <td style={{ padding: "3px 10px", borderBottom: "1px solid #fee2e2" }}>{e.email || "—"}</td>
                          <td style={{ padding: "3px 10px", borderBottom: "1px solid #fee2e2" }}>{e.reason}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ── Employee list ── */}
      {!selectedId && (
        <div>
          {/* ── Unified search bar + controls ── */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 10 }}>
              <div style={{ flex: 1, position: "relative" as const }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" style={{ position: "absolute" as const, left: 10, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }}>
                  <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                </svg>
                <input placeholder="Search name, email, title, ID…" value={search} onChange={e => setSearch(e.target.value)}
                  style={{ ...inputStyle, width: "100%", paddingLeft: 32, boxSizing: "border-box" as const }} />
              </div>
              <select value={sortBy} onChange={e => setSortBy(e.target.value)}
                style={{ ...inputStyle, width: 150, flexShrink: 0 }}>
                <option value="name_asc">Name A→Z</option>
                <option value="name_desc">Name Z→A</option>
                <option value="newest">Newest Joined</option>
                <option value="oldest">Oldest Joined</option>
              </select>
              <div style={{ display: "flex", border: "1px solid #e2e8f0", borderRadius: 8, overflow: "hidden", flexShrink: 0 }}>
                {(["grid", "list"] as const).map(v => (
                  <button key={v} onClick={() => setViewMode(v)} style={{
                    padding: "7px 11px", border: "none", cursor: "pointer",
                    background: viewMode === v ? "#1e3a8a" : "white",
                    color: viewMode === v ? "white" : "#64748b",
                  }}>
                    {v === "grid"
                      ? <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>
                      : <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
                    }
                  </button>
                ))}
              </div>
            </div>
            {/* Department pills */}
            {departments.length > 0 && (
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" as const }}>
                <button onClick={() => setDeptFilter("")} style={{
                  padding: "3px 12px", borderRadius: 20, fontSize: 12, cursor: "pointer", border: "1px solid",
                  background: !deptFilter ? "#1e3a8a" : "white", color: !deptFilter ? "white" : "#64748b",
                  borderColor: !deptFilter ? "#1e3a8a" : "#e2e8f0", fontWeight: 500,
                }}>All</button>
                {departments.map((d: any) => (
                  <button key={d} onClick={() => setDeptFilter(deptFilter === d ? "" : d)} style={{
                    padding: "3px 12px", borderRadius: 20, fontSize: 12, cursor: "pointer", border: "1px solid",
                    background: deptFilter === d ? "#1e3a8a" : "white", color: deptFilter === d ? "white" : "#64748b",
                    borderColor: deptFilter === d ? "#1e3a8a" : "#e2e8f0", fontWeight: 500,
                  }}>{d}</button>
                ))}
              </div>
            )}
          </div>

          {viewMode === "grid" ? (
            <div className="grid-cards" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 14 }}>
              {filtered.map((emp: any) => {
                const dColor = DEPT_COLORS[emp.department] || "#64748b";
                const inactive = emp.is_active === false;
                const isHovered = hoveredId === emp.id;
                const rb = ROLE_BADGE[emp.role] || ROLE_BADGE.employee;
                const showRole = emp.role !== "employee";
                return (
                  <div key={emp.id} onClick={() => openEmployee(emp)}
                    onMouseEnter={() => setHoveredId(emp.id)}
                    onMouseLeave={() => setHoveredId(null)}
                    style={{
                      background: "white", border: "1px solid #e2e8f0", borderRadius: 12,
                      padding: 20, cursor: "pointer", transition: "box-shadow 0.15s",
                      borderTop: `3px solid ${inactive ? "#e2e8f0" : dColor}`,
                      boxShadow: isHovered ? "0 4px 16px rgba(0,0,0,0.1)" : "none",
                      opacity: inactive ? 0.55 : 1, position: "relative" as const,
                    }}>
                    {/* Hover quick actions */}
                    {isHovered && (
                      <div onClick={e => e.stopPropagation()} style={{
                        position: "absolute" as const, top: 10, right: 10,
                        display: "flex", gap: 4,
                      }}>
                        <button onClick={() => openEmployee(emp)} title="Edit profile"
                          style={{ padding: "4px 8px", fontSize: 11, background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 6, cursor: "pointer", color: "#334155" }}>✏ Edit</button>
                        {emp.email && (
                          <a href={`mailto:${emp.email}`} title="Send email"
                            style={{ padding: "4px 8px", fontSize: 11, background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 6, color: "#1d4ed8", textDecoration: "none" }}>✉</a>
                        )}
                      </div>
                    )}
                    <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
                      {emp.picture ? (
                        <img src={`${API_URL}${emp.picture}`} alt={emp.name}
                          onError={(e: any) => { e.target.style.display = "none"; e.target.nextSibling.style.display = "flex"; }}
                          style={{ width: 44, height: 44, borderRadius: "50%", flexShrink: 0, objectFit: "cover" }} />
                      ) : null}
                      <div style={{
                        width: 44, height: 44, borderRadius: "50%", flexShrink: 0,
                        background: inactive ? "#e2e8f0" : `linear-gradient(135deg, ${dColor}, ${NAVY})`,
                        display: emp.picture ? "none" : "flex", alignItems: "center", justifyContent: "center",
                        color: "white", fontWeight: 700, fontSize: 18,
                      }}>
                        {(emp.name || "?")[0].toUpperCase()}
                      </div>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ fontWeight: 600, color: "#1e293b", fontSize: 15, textTransform: "capitalize", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" as const }}>{emp.name}</div>
                        <div style={{ fontSize: 12, color: "#64748b" }}>{emp.designation || "—"}</div>
                      </div>
                    </div>
                    <div style={{ fontSize: 12, color: "#94a3b8", marginBottom: 8, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" as const }}>{emp.email}</div>
                    <div style={{ display: "flex", gap: 5, flexWrap: "wrap" as const, alignItems: "center" }}>
                      {emp.department && (
                        <span style={{ background: `${dColor}18`, color: dColor, border: `1px solid ${dColor}40`, padding: "2px 8px", borderRadius: 12, fontSize: 11, fontWeight: 600 }}>{emp.department}</span>
                      )}
                      {showRole && (
                        <span style={{ background: rb.bg, color: rb.text, border: `1px solid ${rb.border}`, padding: "2px 8px", borderRadius: 6, fontSize: 11, fontWeight: 600 }}>{emp.role.replace(/_/g, " ")}</span>
                      )}
                      {emp.employee_id && <span style={{ background: "#f1f5f9", color: "#64748b", padding: "2px 8px", borderRadius: 12, fontSize: 11 }}>#{emp.employee_id}</span>}
                      {emp.invite_pending && <span style={{ background: "#fef9c3", color: "#854d0e", border: "1px solid #fde047", padding: "2px 8px", borderRadius: 12, fontSize: 11, fontWeight: 600 }}>Setup Pending</span>}
                      {emp.invite_pending && <ResendInviteButton userId={emp.id} name={emp.name} />}
                      {inactive && <span style={{ background: "#fee2e2", color: "#dc2626", border: "1px solid #fca5a5", padding: "2px 8px", borderRadius: 12, fontSize: 11, fontWeight: 600 }}>Inactive</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            /* ── List view ── */
            <div style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 10, overflow: "hidden" }}>
              {filtered.map((emp: any, i: number) => {
                const dColor = DEPT_COLORS[emp.department] || "#64748b";
                const inactive = emp.is_active === false;
                const isHovered = hoveredId === emp.id;
                const rb = ROLE_BADGE[emp.role] || ROLE_BADGE.employee;
                const showRole = emp.role !== "employee";
                return (
                  <div key={emp.id}
                    onMouseEnter={() => setHoveredId(emp.id)}
                    onMouseLeave={() => setHoveredId(null)}
                    style={{
                      display: "flex", alignItems: "center", gap: 12, padding: "10px 16px",
                      borderBottom: i < filtered.length - 1 ? "1px solid #f1f5f9" : "none",
                      background: isHovered ? "#f8fafc" : "white",
                      opacity: inactive ? 0.55 : 1, cursor: "pointer",
                    }}
                    onClick={() => openEmployee(emp)}>
                    <div style={{
                      width: 36, height: 36, borderRadius: "50%", flexShrink: 0,
                      background: inactive ? "#e2e8f0" : `linear-gradient(135deg, ${dColor}, ${NAVY})`,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      color: "white", fontWeight: 700, fontSize: 14,
                    }}>{(emp.name || "?")[0].toUpperCase()}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 13, color: "#1e293b" }}>{emp.name}</div>
                      <div style={{ fontSize: 11, color: "#94a3b8" }}>{emp.designation || emp.email}</div>
                    </div>
                    <div style={{ fontSize: 12, color: "#64748b", width: 180, flexShrink: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" as const }}>{emp.email}</div>
                    <div style={{ display: "flex", gap: 5, alignItems: "center", flexShrink: 0 }}>
                      {emp.department && <span style={{ background: `${dColor}18`, color: dColor, border: `1px solid ${dColor}40`, padding: "2px 8px", borderRadius: 12, fontSize: 11, fontWeight: 600 }}>{emp.department}</span>}
                      {showRole && <span style={{ background: rb.bg, color: rb.text, border: `1px solid ${rb.border}`, padding: "2px 7px", borderRadius: 6, fontSize: 11, fontWeight: 600 }}>{emp.role.replace(/_/g, " ")}</span>}
                      {emp.invite_pending && <span style={{ background: "#fef9c3", color: "#854d0e", padding: "2px 7px", borderRadius: 6, fontSize: 11, fontWeight: 600 }}>Setup Pending</span>}
                      {inactive && <span style={{ background: "#fee2e2", color: "#dc2626", padding: "2px 7px", borderRadius: 6, fontSize: 11, fontWeight: 600 }}>Inactive</span>}
                    </div>
                    {isHovered && (
                      <div onClick={e => e.stopPropagation()} style={{ display: "flex", gap: 4, flexShrink: 0 }}>
                        <button onClick={() => openEmployee(emp)} style={{ padding: "4px 10px", fontSize: 11, background: "#f1f5f9", border: "1px solid #e2e8f0", borderRadius: 6, cursor: "pointer", color: "#334155" }}>Edit</button>
                        {emp.email && <a href={`mailto:${emp.email}`} onClick={e => e.stopPropagation()} style={{ padding: "4px 10px", fontSize: 11, background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 6, color: "#1d4ed8", textDecoration: "none" }}>✉</a>}
                        {emp.invite_pending && (
                          <ResendInviteButton userId={emp.id} name={emp.name} />
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          {filtered.length === 0 && (
            <div style={{ textAlign: "center", padding: "60px 0", color: "#94a3b8" }}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ margin: "0 auto", display: "block" }}>
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
              </svg>
              <div style={{ marginTop: 12, fontSize: 14 }}>No employees found.</div>
            </div>
          )}
        </div>
      )}

      {/* ── Profile / Documents ── */}
      {selectedId && selectedEmployee && (
        <div>
          {/* Sub-tabs */}
          <div className="tab-pill-bar" style={{ marginBottom: 24 }}>
            {(["profile", "documents", "letters"] as Tab[]).map(t => (
              <button key={t} onClick={() => setTab(t)} className={tab === t ? "active" : ""} style={{ textTransform: "capitalize" }}>{t}</button>
            ))}
          </div>

          {/* ── Profile Tab ── */}
          {tab === "profile" && (
            <div className="grid-2col" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>
              {/* Left — basic info */}
              <div style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 12, padding: 24 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
                  <h3 style={{ margin: 0, color: NAVY, fontSize: 16 }}>Personal Information</h3>
                  {isAdmin && (
                    <button onClick={() => setEditMode(m => !m)} style={outlineBtnStyle}>
                      {editMode ? "Cancel" : "✏ Edit"}
                    </button>
                  )}
                </div>

                {/* Read-only fields managed by Workspace → Team */}
                <div style={{ display: "grid", gap: 10, marginBottom: 16 }}>
                  {[
                    ["Name", selectedEmployee.name],
                    ["Employee ID", selectedEmployee.employee_id || "—"],
                    ["Email", selectedEmployee.email],
                    ["Designation", selectedEmployee.designation || "—"],
                    ["Department", selectedEmployee.department || "—"],
                    ["Date of Birth", selectedEmployee.date_of_birth || "—"],
                    ["Joining Date", selectedEmployee.joining_date || "—"],
                    ["Probation End", selectedEmployee.probation_end_date || "—"],
                    ["Manager", selectedEmployee.manager_name || "—"],
                    ["Role", selectedEmployee.role],
                  ].map(([label, value]) => (
                    <div key={label} style={{ display: "flex", borderBottom: "1px solid #f1f5f9", paddingBottom: 8 }}>
                      <span style={{ width: 130, fontSize: 12, color: "#94a3b8", flexShrink: 0 }}>{label}</span>
                      <span style={{ fontSize: 13, color: "#1e293b", fontWeight: 500 }}>{value}</span>
                    </div>
                  ))}
                </div>
                {isAdmin && !editMode && (
                  <p style={{ fontSize: 11, color: "#94a3b8", margin: "0 0 14px", fontStyle: "italic" }}>
                    Click Edit to update all profile fields including Employee ID, designation, department and manager.
                  </p>
                )}

                {/* Editable fields */}
                {editMode ? (
                  <div style={{ display: "grid", gap: 14 }}>
                    {isAdmin && <>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                        <div>
                          <label style={labelStyle}>Employee ID
                            <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 400, color: "#94a3b8" }}>auto-generated if blank</span>
                          </label>
                          <input value={form.employee_id || ""} onChange={e => setForm((f: any) => ({ ...f, employee_id: e.target.value }))}
                            placeholder="Auto (EMP-001)" style={inputStyle} />
                        </div>
                        <div>
                          <label style={labelStyle}>Joining Date</label>
                          <DateInput value={form.joining_date || ""} onChange={e => setForm((f: any) => ({ ...f, joining_date: e.target.value }))} style={inputStyle} />
                        </div>
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                        <div>
                          <label style={labelStyle}>Date of Birth <span style={{ fontWeight: 400, color: "#94a3b8", fontSize: 10 }}>(optional)</span></label>
                          <DateInput value={form.date_of_birth || ""} max={todayStr()} onChange={e => setForm((f: any) => ({ ...f, date_of_birth: e.target.value || null }))} style={inputStyle} />
                        </div>
                        <div>
                          <label style={labelStyle}>Probation End Date <span style={{ fontWeight: 400, color: "#94a3b8", fontSize: 10 }}>(optional)</span></label>
                          <DateInput value={form.probation_end_date || ""} min={form.joining_date || undefined} onChange={e => setForm((f: any) => ({ ...f, probation_end_date: e.target.value || null }))} style={inputStyle} />
                        </div>
                      </div>
                      {form.role === "contractor" && (
                        <>
                          <div style={{ background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 8, padding: "12px 14px" }}>
                            <div style={{ fontSize: 12, fontWeight: 700, color: "#1e3a8a", marginBottom: 10 }}>Contractor Details</div>
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
                              <div>
                                <label style={labelStyle}>Hourly Rate</label>
                                <input type="number" min="0" step="0.01" value={form.hourly_rate ?? ""} onChange={e => setForm((f: any) => ({ ...f, hourly_rate: e.target.value ? parseFloat(e.target.value) : null }))} placeholder="e.g. 500" style={inputStyle} />
                              </div>
                              <div>
                                <label style={labelStyle}>Daily Rate</label>
                                <input type="number" min="0" step="0.01" value={form.daily_rate ?? ""} onChange={e => setForm((f: any) => ({ ...f, daily_rate: e.target.value ? parseFloat(e.target.value) : null }))} placeholder="e.g. 4000" style={inputStyle} />
                              </div>
                              <div>
                                <label style={labelStyle}>Contract End Date</label>
                                <DateInput value={form.contract_end_date || ""} min={form.joining_date || undefined} onChange={e => setForm((f: any) => ({ ...f, contract_end_date: e.target.value || null }))} style={inputStyle} />
                              </div>
                            </div>
                          </div>
                        </>
                      )}
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                        <div>
                          <label style={labelStyle}>Designation</label>
                          <input value={form.designation || ""} onChange={e => setForm((f: any) => ({ ...f, designation: e.target.value }))}
                            placeholder="e.g. Software Engineer" style={inputStyle} />
                        </div>
                        <div>
                          <label style={labelStyle}>Department</label>
                          <DepartmentSelect
                            value={form.department || ""}
                            onChange={name => setForm((f: any) => ({ ...f, department: name }))}
                          />
                        </div>
                      </div>
                      <div>
                        <label style={labelStyle}>Manager</label>
                        <select value={form.manager_id || ""} onChange={e => setForm((f: any) => ({ ...f, manager_id: e.target.value || null }))}
                          style={{ ...inputStyle, cursor: "pointer" }}>
                          <option value="">No manager</option>
                          {(employees as any[]).filter((e: any) => e.id !== selectedId).map((e: any) => (
                            <option key={e.id} value={e.id}>{e.name} {e.designation ? `(${e.designation})` : ""}</option>
                          ))}
                        </select>
                      </div>
                    </>}
                    <div>
                      <label style={labelStyle}>Phone</label>
                      <input value={form.phone || ""} onChange={e => setForm((f: any) => ({ ...f, phone: e.target.value }))} style={inputStyle} />
                    </div>
                    <div>
                      <label style={labelStyle}>Address</label>
                      <textarea value={form.address || ""} onChange={e => setForm((f: any) => ({ ...f, address: e.target.value }))}
                        style={{ ...inputStyle, height: 60, resize: "vertical" }} />
                    </div>
                    <button onClick={() => updateMut.mutate({
                      id: selectedId!,
                      data: { phone: form.phone, address: form.address, emergency_contact_name: form.emergency_contact_name, emergency_contact_phone: form.emergency_contact_phone, bank_name: form.bank_name, bank_account_number: form.bank_account_number, bank_branch_code: form.bank_branch_code },
                      profileData: isAdmin ? { employee_id: form.employee_id, designation: form.designation, department: form.department, joining_date: form.joining_date || null, probation_end_date: form.probation_end_date || null, date_of_birth: form.date_of_birth || null, manager_id: form.manager_id ? Number(form.manager_id) : null, hourly_rate: form.hourly_rate ?? null, daily_rate: form.daily_rate ?? null, contract_end_date: form.contract_end_date || null } : undefined,
                    })} disabled={updateMut.isPending} style={primaryBtnStyle}>
                      {updateMut.isPending ? "Saving…" : "Save Changes"}
                    </button>
                  </div>
                ) : (
                  <div style={{ display: "grid", gap: 10 }}>
                    {[
                      ["Phone", selectedEmployee.phone || "—"],
                      ["Address", selectedEmployee.address || "—"],
                    ].map(([label, value]) => (
                      <div key={label} style={{ display: "flex", borderBottom: "1px solid #f1f5f9", paddingBottom: 8 }}>
                        <span style={{ width: 130, fontSize: 12, color: "#94a3b8", flexShrink: 0 }}>{label}</span>
                        <span style={{ fontSize: 13, color: "#1e293b", fontWeight: 500 }}>{value}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Right — emergency + bank + attendance summary */}
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                {/* Emergency Contact */}
                <div style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 12, padding: 20 }}>
                  <h3 style={{ margin: "0 0 14px", color: NAVY, fontSize: 15 }}>Emergency Contact</h3>
                  {editMode ? (
                    <div style={{ display: "grid", gap: 10 }}>
                      {[
                        { label: "Name", key: "emergency_contact_name" },
                        { label: "Phone", key: "emergency_contact_phone" },
                      ].map(({ label, key }) => (
                        <div key={key}>
                          <label style={labelStyle}>{label}</label>
                          <input value={form[key] || ""} onChange={e => setForm((f: any) => ({ ...f, [key]: e.target.value }))} style={inputStyle} />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ display: "grid", gap: 8 }}>
                      {[
                        ["Name", selectedEmployee.emergency_contact_name || "—"],
                        ["Phone", selectedEmployee.emergency_contact_phone || "—"],
                      ].map(([l, v]) => (
                        <div key={l} style={{ display: "flex" }}>
                          <span style={{ width: 60, fontSize: 12, color: "#94a3b8" }}>{l}</span>
                          <span style={{ fontSize: 13 }}>{v}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Bank Details — admin only */}
                {isAdmin && (
                  <div style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 12, padding: 20 }}>
                    <h3 style={{ margin: "0 0 14px", color: NAVY, fontSize: 15 }}>Bank Details</h3>
                    {editMode ? (
                      <div style={{ display: "grid", gap: 10 }}>
                        {[
                          { label: "Bank Name", key: "bank_name" },
                          { label: "Account Number", key: "bank_account_number" },
                          { label: "Branch / IFSC / Sort Code", key: "bank_branch_code" },
                        ].map(({ label, key }) => (
                          <div key={key}>
                            <label style={labelStyle}>{label}</label>
                            <input value={form[key] || ""} onChange={e => setForm((f: any) => ({ ...f, [key]: e.target.value }))} style={inputStyle} />
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div style={{ display: "grid", gap: 8 }}>
                        {[
                          ["Bank", selectedEmployee.bank_name || "—"],
                          ["Account", selectedEmployee.bank_account_number ? "••••" + selectedEmployee.bank_account_number.slice(-4) : "—"],
                          ["Branch Code", selectedEmployee.bank_branch_code || "—"],
                        ].map(([l, v]) => (
                          <div key={l} style={{ display: "flex" }}>
                            <span style={{ width: 90, fontSize: 12, color: "#94a3b8" }}>{l}</span>
                            <span style={{ fontSize: 13 }}>{v}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

              </div>
            </div>
          )}

          {/* ── Documents Tab ── */}
          {tab === "documents" && (
            <div>
              {/* Upload form */}
              <div style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 12, padding: 20, marginBottom: 20 }}>
                <h3 style={{ margin: "0 0 16px", color: NAVY, fontSize: 15 }}>Upload Document</h3>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr auto", gap: 12, alignItems: "flex-end" }}>
                  <div>
                    <label style={labelStyle}>Document Name *</label>
                    <input value={uploadName} onChange={e => setUploadName(e.target.value)}
                      placeholder="e.g. Employment Contract" style={inputStyle} />
                  </div>
                  <div>
                    <label style={labelStyle}>Type</label>
                    <select value={uploadType} onChange={e => setUploadType(e.target.value)} style={inputStyle}>
                      {["contract", "policy", "certificate", "id", "other"].map(t => (
                        <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label style={labelStyle}>File *</label>
                    <input type="file" onChange={e => setUploadFile(e.target.files?.[0] || null)}
                      style={{ fontSize: 13 }} />
                  </div>
                  <button onClick={handleUploadDoc} disabled={!uploadName || !uploadFile || uploadDocMut.isPending}
                    style={{ ...primaryBtnStyle, padding: "9px 16px" }}>
                    {uploadDocMut.isPending ? "Uploading…" : "Upload"}
                  </button>
                </div>
              </div>

              {/* Documents list */}
              <div style={{ display: "grid", gap: 10 }}>
                {(documents as any[]).map((doc: any) => (
                  <div key={doc.id} style={{
                    background: "white", border: "1px solid #e2e8f0", borderRadius: 10,
                    padding: "14px 18px", display: "flex", alignItems: "center", justifyContent: "space-between",
                  }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <span style={{ fontSize: 22 }}>{docIcon(doc.doc_type)}</span>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 14, color: "#1e293b" }}>{doc.name}</div>
                        <div style={{ fontSize: 12, color: "#94a3b8" }}>
                          {doc.doc_type} · {formatDate(doc.uploaded_at, "short", tz)}
                        </div>
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 8 }}>
                      <AsyncButton onClick={() => handleDownloadDoc(doc.id, doc.name)} style={outlineBtnStyle}>↓ Download</AsyncButton>
                      {isAdmin && (
                        <AsyncButton onClick={async () => { if (await confirm(`Delete "${doc.name}"?`, { confirmLabel: "Delete" })) deleteDocMut.mutate({ userId: selectedId!, docId: doc.id }); }}
                          style={{ ...outlineBtnStyle, color: "#dc2626", borderColor: "#fca5a5" }}>Remove</AsyncButton>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              {(documents as any[]).length === 0 && (
                <div style={{ textAlign: "center", padding: "50px 0", color: "#94a3b8" }}>
                  <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.5" style={{ marginBottom: 10 }}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="9" y1="13" x2="15" y2="13"/><line x1="9" y1="17" x2="13" y2="17"/></svg>
                  <div>No documents uploaded yet.</div>
                </div>
              )}
            </div>
          )}

          {/* ── Letters Tab ── */}
          {tab === "letters" && (
            <div>
              {/* Generate button */}
              <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 16 }}>
                <button onClick={() => { setShowLetterForm(true); setLetterForm({ letter_type: "appointment", params: {} }); }}
                  style={primaryBtnStyle}>
                  + Generate Letter
                </button>
              </div>

              {/* Generate form */}
              {showLetterForm && (
                <div style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 12, padding: 20, marginBottom: 20 }}>
                  <h3 style={{ margin: "0 0 16px", color: NAVY, fontSize: 15 }}>Generate HR Letter</h3>
                  <div style={{ display: "grid", gap: 14 }}>
                    <div>
                      <label style={labelStyle}>Letter Type *</label>
                      <select value={letterForm.letter_type}
                        onChange={e => setLetterForm((f: any) => ({ ...f, letter_type: e.target.value }))}
                        style={inputStyle}>
                        {(letterTypes as any[]).map((t: any) => (
                          <option key={t.value} value={t.value}>{t.label}</option>
                        ))}
                      </select>
                    </div>

                    {templateChoices.length > 0 && (
                      <div>
                        <label style={labelStyle}>Template</label>
                        <select
                          value={letterForm.use_template === false ? "__freeform__"
                                 : (letterForm.template_id ?? "__default__")}
                          onChange={e => {
                            const v = e.target.value;
                            setLetterForm((f: any) => ({
                              ...f,
                              use_template: v !== "__freeform__",
                              template_id: (v === "__freeform__" || v === "__default__")
                                ? undefined : Number(v),
                            }));
                          }}
                          style={inputStyle}>
                          <option value="__default__">
                            {defaultTemplate ? `${defaultTemplate.name} (default)` : "No template — AI writes it all"}
                          </option>
                          {templateChoices
                            .filter((tpl: any) => !defaultTemplate || tpl.id !== defaultTemplate.id)
                            .map((tpl: any) => (
                              <option key={tpl.id} value={tpl.id}>{tpl.name}</option>
                            ))}
                          <option value="__freeform__">— AI freeform (ignore templates) —</option>
                        </select>
                        {defaultTemplate && letterForm.use_template !== false
                          && (letterForm.template_id ?? defaultTemplate.id) === defaultTemplate.id && (
                          <div style={{ fontSize: 11.5, color: "#64748b", marginTop: 4 }}>
                            {defaultTemplate.block_count - defaultTemplate.ai_block_count} fixed
                            {" "}sections · {defaultTemplate.ai_block_count} written by AI · v{defaultTemplate.version}
                            {!defaultTemplate.uses_ai && " · uses no AI credits"}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Conditional param fields based on letter type */}
                    {["increment"].includes(letterForm.letter_type) && (
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
                        <div>
                          <label style={labelStyle}>Old Salary</label>
                          <input placeholder="50000" style={inputStyle}
                            onChange={e => setLetterForm((f: any) => ({ ...f, params: { ...f.params, old_salary: e.target.value } }))} />
                        </div>
                        <div>
                          <label style={labelStyle}>New Salary *</label>
                          <input placeholder="60000" style={inputStyle}
                            onChange={e => setLetterForm((f: any) => ({ ...f, params: { ...f.params, new_salary: e.target.value } }))} />
                        </div>
                        <div>
                          <label style={labelStyle}>Effective Date</label>
                          <DateInput style={inputStyle}
                            onChange={e => setLetterForm((f: any) => ({ ...f, params: { ...f.params, effective_date: e.target.value } }))} />
                        </div>
                      </div>
                    )}
                    {["promotion", "transfer"].includes(letterForm.letter_type) && (
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
                        <div>
                          <label style={labelStyle}>New Designation</label>
                          <input placeholder="Senior Engineer" style={inputStyle}
                            onChange={e => setLetterForm((f: any) => ({ ...f, params: { ...f.params, new_designation: e.target.value } }))} />
                        </div>
                        <div>
                          <label style={labelStyle}>New Department</label>
                          <input placeholder="Engineering" style={inputStyle}
                            onChange={e => setLetterForm((f: any) => ({ ...f, params: { ...f.params, new_department: e.target.value } }))} />
                        </div>
                        <div>
                          <label style={labelStyle}>Effective Date</label>
                          <DateInput style={inputStyle}
                            onChange={e => setLetterForm((f: any) => ({ ...f, params: { ...f.params, effective_date: e.target.value } }))} />
                        </div>
                      </div>
                    )}
                    {["appointment", "contract", "relieving"].includes(letterForm.letter_type) && (
                      <div>
                        <label style={labelStyle}>Effective Date</label>
                        <DateInput style={{ ...inputStyle, maxWidth: 220 }}
                          onChange={e => setLetterForm((f: any) => ({ ...f, params: { ...f.params, effective_date: e.target.value } }))} />
                      </div>
                    )}
                    {["salary_certificate", "noc"].includes(letterForm.letter_type) && (
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                        <div>
                          <label style={labelStyle}>Monthly Salary (optional)</label>
                          <input placeholder="60000" style={inputStyle}
                            onChange={e => setLetterForm((f: any) => ({ ...f, params: { ...f.params, new_salary: e.target.value } }))} />
                        </div>
                        {letterForm.letter_type === "noc" && (
                          <div>
                            <label style={labelStyle}>Purpose / Reason</label>
                            <input placeholder="apply for a bank loan" style={inputStyle}
                              onChange={e => setLetterForm((f: any) => ({ ...f, params: { ...f.params, reason: e.target.value } }))} />
                          </div>
                        )}
                      </div>
                    )}
                    {letterForm.letter_type === "warning" && (
                      <div>
                        <label style={labelStyle}>Reason / Issue</label>
                        <input placeholder="repeated tardiness / performance issue" style={inputStyle}
                          onChange={e => setLetterForm((f: any) => ({ ...f, params: { ...f.params, reason: e.target.value } }))} />
                      </div>
                    )}

                    <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
                      <button type="button" onClick={() => setShowLetterForm(false)} style={outlineBtnStyle}>Cancel</button>
                      <button
                        disabled={generateLetterMut.isPending}
                        onClick={() => generateLetterMut.mutate({ employee_id: selectedId!, letter_type: letterForm.letter_type, params: letterForm.params, template_id: letterForm.template_id, use_template: letterForm.use_template !== false })}
                        style={primaryBtnStyle}>
                        {generateLetterMut.isPending ? "Generating…" : "✨ Generate"}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Edit modal */}
              {editLetter && (
                <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center" }}
                  onClick={() => setEditLetter(null)}>
                  <div style={{ background: "white", borderRadius: 14, padding: 28, width: "min(720px, calc(100vw - 32px))", maxHeight: "90vh", overflowY: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }}
                    onClick={e => e.stopPropagation()}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                      <h3 style={{ margin: 0, color: NAVY, fontSize: 16 }}>{editLetter.letter_type_label}</h3>
                      <select value={editLetter.status}
                        onChange={e => setEditLetter((l: any) => ({ ...l, status: e.target.value }))}
                        style={{ ...inputStyle, width: "auto", fontSize: 12 }}>
                        <option value="draft">Draft</option>
                        <option value="issued">Issued</option>
                      </select>
                    </div>
                    <textarea
                      value={editLetter.content || ""}
                      onChange={e => setEditLetter((l: any) => ({ ...l, content: e.target.value }))}
                      style={{ ...inputStyle, minHeight: 360, fontFamily: "monospace", fontSize: 12, resize: "vertical" }}
                    />
                    <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 14 }}>
                      <button onClick={() => setEditLetter(null)} style={outlineBtnStyle}>Cancel</button>
                      <button onClick={() => handleDownloadLetter(editLetter.id)} style={outlineBtnStyle}>
                        {downloadingLetter === editLetter.id ? "Downloading…" : "⬇ PDF"}
                      </button>
                      <AsyncButton onClick={async () => {
                        // The original body still sits in the letters query cache —
                        // editLetter holds the in-progress copy — so comparing the two
                        // tells us whether this save will void a live signature (AC5).
                        const original = (letters as any[]).find((x: any) => x.id === editLetter.id)?.content;
                        const willVoid = editLetter.content !== original
                          && (editLetter.signature_status === "pending" || editLetter.signature_status === "signed");
                        if (willVoid && !(await confirm(
                          editLetter.signature_status === "signed"
                            ? "This letter has already been signed. Editing the text voids that signature — the employee's signature will be removed from the PDF and you'll need to request it again."
                            : "A signature is pending on this letter. Editing the text voids that request — the employee can no longer sign it and you'll need to request a new signature.",
                          { confirmLabel: "Edit and void signature" },
                        ))) return;
                        updateLetterMut.mutate({ id: editLetter.id, data: { content: editLetter.content, status: editLetter.status } });
                      }} disabled={updateLetterMut.isPending} style={primaryBtnStyle}>
                        {updateLetterMut.isPending ? "Saving…" : "Save"}
                      </AsyncButton>
                    </div>
                  </div>
                </div>
              )}

              {/* Company signatory picker (Package E — AC18) */}
              {countersignFor && (
                <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
                  onClick={() => setCountersignFor(null)}>
                  <div style={{ background: "white", borderRadius: 14, padding: 28, width: "min(460px, calc(100vw - 32px))", boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }}
                    onClick={e => e.stopPropagation()}>
                    <h3 style={{ margin: "0 0 6px", color: NAVY, fontSize: 16 }}>Request signatures</h3>
                    <p style={{ margin: "0 0 16px", fontSize: 13, color: "#64748b" }}>
                      {countersignFor.letter_type_label} is signed by both parties. {selectedEmployee?.name} signs
                      as the employee — choose who signs on the company's behalf.
                    </p>
                    <label style={labelStyle}>Company signatory</label>
                    <select value={countersignerId}
                      onChange={e => setCountersignerId(e.target.value ? Number(e.target.value) : "")}
                      style={inputStyle}>
                      <option value="">— select —</option>
                      {(countersigners as any[])
                        .filter((u: any) => u.id !== countersignFor.employee_id)
                        .map((u: any) => (
                          <option key={u.id} value={u.id}>{u.name} ({u.role})</option>
                        ))}
                    </select>
                    <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 20 }}>
                      <button onClick={() => setCountersignFor(null)} style={outlineBtnStyle}>Cancel</button>
                      <button
                        disabled={!countersignerId || requestSignatureMut.isPending}
                        onClick={() => requestSignatureMut.mutate({
                          id: countersignFor.id, countersignerId: Number(countersignerId),
                        })}
                        style={{ ...primaryBtnStyle, opacity: countersignerId ? 1 : 0.5 }}>
                        {requestSignatureMut.isPending ? "Requesting…" : "Request signatures"}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Signature history (Package D — AC15/AC16) */}
              {historyLetter && (
                <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
                  onClick={() => setHistoryLetter(null)}>
                  <div style={{ background: "white", borderRadius: 14, padding: 28, width: "min(760px, calc(100vw - 32px))", maxHeight: "88vh", overflowY: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }}
                    onClick={e => e.stopPropagation()}>
                    <h3 style={{ margin: "0 0 4px", color: NAVY, fontSize: 16 }}>Signature history</h3>
                    <p style={{ margin: "0 0 18px", fontSize: 13, color: "#64748b" }}>
                      {historyLetter.letter_type_label} — {selectedEmployee?.name}
                    </p>
                    {sigEventsLoading ? (
                      <div style={{ padding: 30, textAlign: "center", color: "#94a3b8" }}>Loading…</div>
                    ) : (sigEvents as any[]).length === 0 ? (
                      <div style={{ padding: 30, textAlign: "center", color: "#94a3b8" }}>No signature activity yet.</div>
                    ) : (
                      <div style={{ overflowX: "auto" }}>
                        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                          <thead>
                            <tr style={{ textAlign: "left", color: "#475569", borderBottom: "1.5px solid #e2e8f0" }}>
                              <th style={{ padding: "8px 10px" }}>Cycle</th>
                              <th style={{ padding: "8px 10px" }}>Event</th>
                              <th style={{ padding: "8px 10px" }}>When</th>
                              <th style={{ padding: "8px 10px" }}>IP / location</th>
                              <th style={{ padding: "8px 10px" }}>Consent</th>
                              <th style={{ padding: "8px 10px" }}>Document hash</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(sigEvents as any[]).map((e: any, i: number) => (
                              <tr key={i} style={{ borderBottom: "1px solid #f1f5f9", color: "#334155" }}>
                                <td style={{ padding: "8px 10px", color: "#94a3b8" }}>
                                  #{e.cycle}
                                  {e.envelope_status === "voided" && <span style={{ color: "#b45309" }}> (voided)</span>}
                                </td>
                                <td style={{ padding: "8px 10px", fontWeight: 600 }}>{e.event_type}</td>
                                {/* Time, not just the date: this table exists to establish the order
                                    and moment of each signing act, and the certificate records
                                    it to the minute. A date alone cannot settle "which came
                                    first". */}
                                <td style={{ padding: "8px 10px", whiteSpace: "nowrap" }}>{formatDateTime(e.occurred_at, tz)}</td>
                                <td style={{ padding: "8px 10px" }}>
                                  {e.ip_address || "—"}{e.location ? ` · ${e.location}` : ""}
                                </td>
                                <td style={{ padding: "8px 10px" }}>{e.consent_text_version || "—"}</td>
                                <td style={{ padding: "8px 10px", fontFamily: "monospace", fontSize: 11 }}
                                  title={e.document_sha256 || ""}>
                                  {e.document_sha256 ? `${e.document_sha256.slice(0, 12)}…` : "—"}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                    <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 18 }}>
                      <button onClick={() => setHistoryLetter(null)} style={outlineBtnStyle}>Close</button>
                    </div>
                  </div>
                </div>
              )}

              {/* Letters list */}
              <div style={{ display: "grid", gap: 10 }}>
                {(letters as any[]).map((l: any) => (
                  <div key={l.id} style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 10, padding: "14px 18px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <span style={{ fontSize: 22 }}>📝</span>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 14, color: "#1e293b" }}>{l.letter_type_label}</div>
                        <div style={{ fontSize: 12, color: "#94a3b8" }}>
                          {formatDate(l.created_at, "short", tz)}
                          {" · "}
                          <span style={{
                            padding: "1px 8px", borderRadius: 10, fontSize: 11, fontWeight: 600,
                            background: l.status === "issued" ? "#f0fdf4" : "#f8fafc",
                            color: l.status === "issued" ? "#166534" : "#64748b",
                            border: `1px solid ${l.status === "issued" ? "#86efac" : "#e2e8f0"}`,
                          }}>{l.status}</span>
                        </div>
                        {/* Which template version produced this letter (AC31), and
                            which AI blocks fell back to authored text (AC30). */}
                        {(l.template || (l.ai_fallback_blocks || []).length > 0) && (
                          <div style={{ fontSize: 11.5, color: "#0369a1", marginTop: 4 }}>
                            {l.template && <>From <strong>{l.template.template_name}</strong> v{l.template.version}</>}
                            {(l.ai_fallback_blocks || []).length > 0 && (
                              <> · {l.ai_fallback_blocks.length} section
                                {l.ai_fallback_blocks.length === 1 ? "" : "s"} used fallback text</>
                            )}
                          </div>
                        )}
                        <div style={{ display: "none" }}>
                          {/* One chip per required signature. A contract has two
                              parties, and a single chip could only ever tell
                              half the story (AC21). */}
                          {l.needs_signature && (l.signatures || []).filter((s: any) => s.status).map((s: any) => {
                            const chip = SIG_CHIP[s.status] || SIG_CHIP.pending;
                            const prefix = (l.signatures || []).length > 1 ? `${s.label}: ` : "";
                            return (
                              <React.Fragment key={s.purpose}>
                                {" · "}
                                <span
                                  title={s.rejection_reason || (s.signer_name ? `${s.label} — ${s.signer_name}` : s.label)}
                                  style={{
                                    padding: "1px 8px", borderRadius: 10, fontSize: 11, fontWeight: 600,
                                    background: chip.bg, color: chip.fg, border: `1px solid ${chip.border}`,
                                  }}>
                                  {prefix}{chip.label}
                                </span>
                              </React.Fragment>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 8 }}>
                      <button onClick={() => setEditLetter(l)} style={outlineBtnStyle}>✏ Edit</button>
                      <button onClick={() => handleDownloadLetter(l.id)} disabled={downloadingLetter === l.id} style={outlineBtnStyle}>
                        {downloadingLetter === l.id ? "…" : "⬇ PDF"}
                      </button>
                      {/* Derived from every required signature, not just the
                          employee's: on a contract the employee can have signed
                          while the company declined, and keying off the
                          acknowledgement alone would strand HR with no way to
                          ask again. */}
                      {l.needs_signature && l.status === "issued"
                        && !(l.signatures || []).some((s: any) => s.status === "pending")
                        && !l.fully_executed && (
                        <button
                          onClick={() => l.needs_countersignature
                            ? (setCountersignFor(l), setCountersignerId(""))
                            : requestSignatureMut.mutate({ id: l.id })}
                          disabled={requestSignatureMut.isPending}
                          style={{ ...outlineBtnStyle, color: "#0f766e", borderColor: "#5eead4" }}
                        >
                          {requestSignatureMut.isPending ? "…" : "✍️ Request Signature"}
                        </button>
                      )}
                      {l.needs_signature && (l.signatures || []).some((s: any) => s.status) && (
                        <button onClick={() => setHistoryLetter(l)} style={outlineBtnStyle}
                          title="Signature audit trail — who signed, when, and from where">
                          🕐 History
                        </button>
                      )}
                      {l.needs_signature && (l.signatures || []).some((s: any) => s.status === "pending") && (
                        <button
                          onClick={() => resendSignatureMut.mutate(l.id)}
                          disabled={resendSignatureMut.isPending}
                          title="Notify the employee again — useful if the first email bounced"
                          style={{ ...outlineBtnStyle, color: "#0f766e", borderColor: "#5eead4" }}
                        >
                          {resendSignatureMut.isPending ? "…" : "↻ Resend"}
                        </button>
                      )}
                      <AsyncButton onClick={async () => { if (await confirm("Delete this letter?", { confirmLabel: "Delete" })) deleteLetterMut.mutate(l.id); }}
                        style={{ ...outlineBtnStyle, color: "#dc2626", borderColor: "#fca5a5" }}>Remove</AsyncButton>
                    </div>
                  </div>
                ))}
              </div>
              {(letters as any[]).length === 0 && !showLetterForm && (
                <div style={{ textAlign: "center", padding: "50px 0", color: "#94a3b8" }}>
                  <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.5" style={{ marginBottom: 10 }}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="9" y1="13" x2="15" y2="13"/><line x1="9" y1="17" x2="11" y2="17"/><path d="M17 17l2 2 4-4" stroke="#cbd5e1"/></svg>
                  <div>No letters generated yet.</div>
                  <div style={{ fontSize: 12, marginTop: 6 }}>Click "Generate Letter" to create one.</div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function docIcon(type: string) {
  const icons: Record<string, string> = { contract: "📝", policy: "📋", certificate: "🏆", id: "🪪", other: "📄" };
  return icons[type] || "📄";
}

// Signature-status chip. A fourth state (voided) pushed this past what nested
// ternaries could carry legibly — "voided" means an HR content edit superseded
// a signature that had already been requested or given.
const SIG_CHIP: Record<string, { label: string; bg: string; fg: string; border: string }> = {
  signed:   { label: "✓ Signed",             bg: "#f0fdf4", fg: "#166534", border: "#86efac" },
  declined: { label: "✗ Declined",           bg: "#fef2f2", fg: "#dc2626", border: "#fecaca" },
  voided:   { label: "⚠ Signature voided",   bg: "#f8fafc", fg: "#475569", border: "#cbd5e1" },
  pending:  { label: "⏳ Awaiting signature", bg: "#fffbeb", fg: "#b45309", border: "#fde68a" },
};

const primaryBtnStyle: React.CSSProperties = {
  background: `linear-gradient(135deg, ${NAVY}, #00c9a7)`,
  color: "white", border: "none", padding: "10px 20px",
  borderRadius: 8, cursor: "pointer", fontWeight: 600, fontSize: 14,
};
const outlineBtnStyle: React.CSSProperties = {
  background: "white", border: "1px solid #e2e8f0", color: "#64748b",
  padding: "6px 14px", borderRadius: 6, cursor: "pointer", fontSize: 13,
};
const labelStyle: React.CSSProperties = {
  display: "block", marginBottom: 5, fontSize: 12, fontWeight: 600, color: "#475569",
};
const inputStyle: React.CSSProperties = {
  width: "100%", padding: "8px 10px", border: "1px solid #e2e8f0", borderRadius: 6,
  fontSize: 13, color: "#1e293b", background: "#fff", boxSizing: "border-box",
};
